import React, { useEffect, useRef, useState } from "react";

import { ChevronLeft, ChevronDown, Plus, Minus } from "lucide-react";
import SymbolSelectionScreen from "./components/SymbolSelectionScreen";
import OrderTypeContentMapper from "./components/OrderTypeContentMapper";
import TickChart from "./components/TickChart";
import { useVybeTraderClient } from "@/vybe-trader-client/connection/hooks/useVybeTraderClient";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client/subscribed-symbols/stores/useSubscribedSymbolsStore";
import {
  VybeOrderType,
} from "@/vybe-trader-client/trade-history/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn-components/ui/select";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { Label } from "@/shadcn-components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shadcn-components/ui/card";
import { Separator } from "@/shadcn-components/ui/separator";
import { toast } from "sonner";
import { InvestorReadOnlyNotice } from "@/components/common/InvestorBadge";
import { useIsInvestorAccount } from "@/vybe-trader-client/account-information/hooks/useAccountInformation";
import {
  calculateDefaultSlPrice,
  calculateDefaultTpPrice,
  validateSLTPPrices,
} from "@/lib/chart-helpers/calculateSLTPValues";

interface TradeOrderScreenProps {
  symbol?: string;
  onBack: () => void;
  type?: "new" | "modify" | "closeBy";
  tradeData?: any; // Dodany prop dla danych trade'a
  initialOrderType?: string; // np. "Buy Limit", "Sell Stop", etc.
  initialPrice?: number; // wartość dla price
  initialStopLoss?: number; // wartość dla stop loss
  initialTakeProfit?: number; // wartość dla take profit
  onPriceChange?: (price: number) => void;
  onStopLossChange?: (price: number) => void;
  onTakeProfitChange?: (price: number) => void;
  isDesktop?: boolean; // Dodany prop dla desktop
  /**
   * Modify only: seed the stop loss as REMOVED even though `tradeData` still carries one — the chart
   * toolbar's SL toggle turned it off and the trader is confirming that removal.
   */
  clearStopLoss?: boolean;
  /** Modify only: same for the take profit. */
  clearTakeProfit?: boolean;
}

const PENDING_ORDER_TYPE_TO_VYBE: Record<string, VybeOrderType> = {
  "Buy Limit": VybeOrderType.BuyLimit,
  "Sell Limit": VybeOrderType.SellLimit,
  "Buy Stop": VybeOrderType.BuyStop,
  "Sell Stop": VybeOrderType.SellStop,
  "Buy Stop Limit": VybeOrderType.BuyStopLimit,
  "Sell Stop Limit": VybeOrderType.SellStopLimit,
};

export default function TradeOrderScreen({
  symbol,
  onBack,
  type = "new",
  tradeData,
  initialOrderType,
  initialPrice,
  initialStopLoss,
  initialTakeProfit,
  onPriceChange,
  onStopLossChange,
  onTakeProfitChange,
  isDesktop = false,
  clearStopLoss = false,
  clearTakeProfit = false,
}: TradeOrderScreenProps) {
  // Subscribe to specific symbol data - this will auto-update when quote changes
  const getSubscribedSymbol = useSubscribedSymbolsStore(
    (state) => state.getSubscribedSymbol
  );

  // Get subscribed symbols
  const getSubscribedSymbols = () => {
    const store = useSubscribedSymbolsStore.getState();
    return store.allSubscribed.filter((entry) => entry.quote);
  };

  const subscribedSymbols = getSubscribedSymbols();
  const availableSymbols = subscribedSymbols
    .map((entry) => entry.symbol.currency)
    .sort();

  // States
  const [currentSymbol, setCurrentSymbol] = useState(
    symbol || availableSymbols[0] || ""
  );
  const [volume, setVolume] = useState(0.01);
  const [orderType, setOrderType] = useState<string>(
    initialOrderType ||
      (type === "modify"
        ? "Modify"
        : type === "closeBy"
        ? "Close By"
        : "Market Execution")
  );
  const [showSymbolSelection, setShowSymbolSelection] = useState(false);
  const isInvestor = useIsInvestorAccount();
  const [volumeInput, setVolumeInput] = useState("0.01");
  const [stopLoss, setStopLoss] = useState<number | null>(
    clearStopLoss
      ? null
      : initialStopLoss ||
          (tradeData && tradeData.stopLoss > 0 ? tradeData.stopLoss : null)
  );
  const [takeProfit, setTakeProfit] = useState<number | null>(
    clearTakeProfit
      ? null
      : initialTakeProfit ||
          (tradeData && tradeData.takeProfit > 0 ? tradeData.takeProfit : null)
  );
  const [stopLossInput, setStopLossInput] = useState(
    clearStopLoss
      ? ""
      : initialStopLoss?.toFixed(5) ||
          (tradeData && tradeData.stopLoss > 0 ? tradeData.stopLoss.toFixed(5) : "")
  );
  const [takeProfitInput, setTakeProfitInput] = useState(
    clearTakeProfit
      ? ""
      : initialTakeProfit?.toFixed(5) ||
          (tradeData && tradeData.takeProfit > 0
            ? tradeData.takeProfit.toFixed(5)
            : "")
  );
  const [price, setPrice] = useState<number | null>(initialPrice || null);
  const [priceInput, setPriceInput] = useState(initialPrice?.toFixed(5) || "");
  const [stopLimitPrice, setStopLimitPrice] = useState<number | null>(null);
  const [stopLimitPriceInput, setStopLimitPriceInput] = useState("");

  // Get symbol data for live prices

  const entry = useSubscribedSymbolsStore((state) =>
    state.byCurrency.get(currentSymbol)
  );
  const symbolData = getSubscribedSymbol(currentSymbol);
  const currentQuote = symbolData?.quote || null;
  const digits = symbolData?.symbol?.digits ?? 5;

  // Get live prices from quote
  const bidPrice = entry?.quote?.bid ?? 0;
  const askPrice = entry?.quote?.ask ?? 0;

  // Format price using digits from store
  const formatPrice = (price: number): string => {
    if (!price) return "---";
    return price.toFixed(digits);
  };

  /**
   * Modify: seed BOTH levels with a side-aware default (~10 pips) for anything the position doesn't
   * already carry, so the trader nudges a sane number instead of typing one from scratch.
   *
   * Side-aware is the whole point: the previous screens anchored SL below the market and TP above it
   * regardless of direction, so on a SELL the "default" stop sat on the wrong side and the engine
   * triggered it the instant it was saved — the position vanished a second after the trader pressed
   * Save (SR Global, 2026-09-09; login 29 lost 5 positions to it). calculateDefault*Price picks the
   * correct side from orderType.
   */
  const seededDefaultsRef = useRef(false);
  useEffect(() => {
    if (type !== "modify" || !tradeData) return;
    if (seededDefaultsRef.current) return;
    // Wait for a live quote — the anchor and the 10-pip distance are both priced off it.
    if (!currentQuote) return;
    seededDefaultsRef.current = true;

    // 10 pips at this symbol's precision (MT convention: 1 pip = 10 points).
    const params = { quote: currentQuote, digits, points: 100 };

    if (!clearStopLoss && !(tradeData.stopLoss > 0) && !stopLoss) {
      const sl = calculateDefaultSlPrice(tradeData, params);
      if (sl > 0) {
        setStopLoss(sl);
        setStopLossInput(sl.toFixed(digits));
      }
    }
    if (!clearTakeProfit && !(tradeData.takeProfit > 0) && !takeProfit) {
      const tp = calculateDefaultTpPrice(tradeData, params);
      if (tp > 0) {
        setTakeProfit(tp);
        setTakeProfitInput(tp.toFixed(digits));
      }
    }
  }, [
    type,
    tradeData,
    currentQuote,
    digits,
    stopLoss,
    takeProfit,
    clearStopLoss,
    clearTakeProfit,
  ]);

  // Get client for sending orders
  const { client } = useVybeTraderClient();
  const [isTrading, setIsTrading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Handle order submission
  const handleOrder = async (tradeType?: "Buy" | "Sell") => {
    if (!currentSymbol) {
      setErrorMessage("No symbol selected");
      return;
    }

    if (!client || !client.isConnected()) {
      setErrorMessage("Not connected to server");
      return;
    }

    const lots = parseFloat(volumeInput);

    // An SL/TP modify needs neither a live quote nor a volume — only new
    // orders do, so skip these guards for the modify branch.
    if (type !== "modify") {
      if (!currentQuote || bidPrice === 0 || askPrice === 0) {
        setErrorMessage("No quote available");
        return;
      }

      if (isNaN(lots) || lots <= 0) {
        setErrorMessage("Invalid lot size");
        return;
      }
    }

    // MT5 parity: a stop on the wrong side of the market is INVALID and must be refused, not sent.
    // ViiSync accepts whatever it is given and its trigger gate fires immediately, so an invalid level
    // does not error — it closes the position at market. Refusing here is what makes "modify" safe.
    if (type === "modify" && tradeData) {
      const { isValid, errors } = validateSLTPPrices(
        tradeData,
        stopLoss || 0,
        takeProfit || 0
      );
      if (!isValid) {
        setErrorMessage(errors.join(". "));
        return;
      }
    }

    setIsTrading(true);
    setErrorMessage("");

    try {
      // Handle modify order type
      if (type === "modify" && tradeData) {
        const modifyData = {
          Ticket: tradeData.ticket,
          Symbol: tradeData.symbol,
          Lots: tradeData.lots,
          Price: tradeData.openPrice,
          OrderType: tradeData.orderType, // Format the order type
          StopLoss: stopLoss || 0,
          TakeProfit: takeProfit || 0,
          // An emptied level on a position that HAS one is a removal. 0 alone means "not specified" on
          // the wire (the gateways map it to "leave unchanged"), so removal needs its own flag.
          ClearStopLoss: !stopLoss && tradeData.stopLoss > 0,
          ClearTakeProfit: !takeProfit && tradeData.takeProfit > 0,
          Comment: "Modified via MT6 Trading App",
        };

        // Sending modify request
        await client.modifyOrder(modifyData);

        // Neutral acknowledgement — the engine's verdict arrives on
        // VybeOnOrderModify and is toasted from the response handler.
        toast.info(`Update sent`, {
          description: `SL/TP update sent for ticket #${tradeData.ticket}`,
        });

        // Close dialog after successful modification
        onBack();
        return;
      }

      // Handle regular orders (new trades)
      let orderData: any = {
        Symbol: currentSymbol,
        Lots: lots,
        Slippage: 100,
        Comment: "MT6 Trade Order",
      };

      if (orderType === "Market Execution") {
        if (!tradeType) {
          setErrorMessage("Trade type required for market execution");
          return;
        }

        // Market execution uses ask price for buy, bid price for sell
        const orderPrice = tradeType === "Buy" ? askPrice : bidPrice;

        orderData = {
          ...orderData,
          OrderType: tradeType == "Buy" ? 0 : 1,
          Price: orderPrice,
          StopLoss: stopLoss || 0,
          TakeProfit: takeProfit || 0,
        };
      } else {
        // Pending orders
        if (!price || price <= 0) {
          setErrorMessage("Price is required for pending orders");
          return;
        }

        const pendingOrderType = PENDING_ORDER_TYPE_TO_VYBE[orderType];
        if (pendingOrderType === undefined) {
          setErrorMessage(`Unsupported pending order type: ${orderType}`);
          return;
        }

        orderData = {
          ...orderData,
          OrderType: pendingOrderType,
          Price: price,
          StopLoss: stopLoss || 0,
          TakeProfit: takeProfit || 0,
        };

        // Add stop limit price for stop limit orders
        if (orderType.includes("Stop Limit") && stopLimitPrice) {
          orderData.StopLimit = stopLimitPrice;
        }
      }

      await client.sendOrder(orderData);

      // Show success toast
      toast.success(`Order placed successfully`, {
        description: `${
          orderType === "Market Execution" ? tradeType : orderType
        } order for ${orderData.Lots} lots of ${currentSymbol}`,
      });

      // Close dialog after successful order
      onBack();

      // Reset form after successful order
      setVolume(0.01);
      setVolumeInput("0.01");
      setStopLoss(null);
      setStopLossInput("");
      setTakeProfit(null);
      setTakeProfitInput("");
      setPrice(null);
      setPriceInput("");
      setStopLimitPrice(null);
      setStopLimitPriceInput("");

      setErrorMessage("");
    } catch (error) {
      console.error("Trading error:", error);
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to place order"
      );
    } finally {
      setIsTrading(false);
    }
  };

  const handleSellByMarket = () => {
    handleOrder("Sell");
  };

  const handleBuyByMarket = () => {
    handleOrder("Buy");
  };

  const handlePlaceOrder = () => {
    handleOrder();
  };

  const handleSaveChanges = () => {
    // Dla typu modify używamy tej samej logiki co handleOrder, ale bez tradeType
    handleOrder();
  };

  // Get order type options based on type prop
  const getOrderTypeOptions = () => {
    const allOptions = [
      {
        value: "Market Execution",
        label: "Market Execution",
        color: "text-muted-foreground",
      },
      {
        value: "Buy Limit",
        label: "Buy Limit",
        color: "text-blue-600 dark:text-blue-400",
      },
      {
        value: "Sell Limit",
        label: "Sell Limit",
        color: "text-red-600 dark:text-red-400",
      },
      {
        value: "Buy Stop",
        label: "Buy Stop",
        color: "text-blue-600 dark:text-blue-400",
      },
      {
        value: "Sell Stop",
        label: "Sell Stop",
        color: "text-red-600 dark:text-red-400",
      },
      {
        value: "Buy Stop Limit",
        label: "Buy Stop Limit",
        color: "text-blue-600 dark:text-blue-400",
      },
      {
        value: "Sell Stop Limit",
        label: "Sell Stop Limit",
        color: "text-red-600 dark:text-red-400",
      },
      { value: "Modify", label: "Modify", color: "text-muted-foreground" },
      { value: "Close By", label: "Close By", color: "text-muted-foreground" },
    ];

    if (type === "new") {
      return allOptions.filter(
        (option) => !["Modify", "Close By"].includes(option.value)
      );
    }
    return allOptions;
  };

  const orderTypeOptions = getOrderTypeOptions();

  // Get symbol description
  const getSymbolDescription = (symbolCurrency: string) => {
    const symbolEntry = subscribedSymbols.find(
      (entry) => entry.symbol.currency === symbolCurrency
    );
    return symbolEntry?.symbol.description || symbolCurrency;
  };

  // Volume control helpers
  const getLotSizeAdjustments = (currentVolume: number) => {
    if (currentVolume < 1) {
      return { decrease: [-0.5, -0.1], increase: [0.1, 0.5] };
    } else if (currentVolume >= 1 && currentVolume < 10) {
      return { decrease: [-5, -1], increase: [1, 5] };
    } else if (currentVolume >= 10 && currentVolume < 50) {
      return { decrease: [-10, -1], increase: [1, 10] };
    } else if (currentVolume >= 50 && currentVolume < 100) {
      return { decrease: [-25, -10], increase: [10, 25] };
    } else {
      return { decrease: [-100, -50], increase: [50, 100] };
    }
  };

  const handleVolumeChange = (change: number) => {
    let currentVol = volume;
    const inputVal = parseFloat(volumeInput);
    if (!isNaN(inputVal) && inputVal >= 0.01 && inputVal <= 100) {
      currentVol = inputVal;
    }

    const newVolume = Math.max(0.01, Math.min(100, currentVol + change));
    setVolume(Number(newVolume.toFixed(2)));
    setVolumeInput(newVolume.toFixed(2));
  };

  const handleVolumeInputChange = (value: string) => {
    setVolumeInput(value);
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0.01 && numValue <= 100) {
      setVolume(Number(numValue.toFixed(2)));
    }
  };

  const handleVolumeInputBlur = () => {
    const numValue = parseFloat(volumeInput);
    if (isNaN(numValue) || numValue < 0.01) {
      setVolume(0.01);
      setVolumeInput("0.01");
    } else if (numValue > 100) {
      setVolume(100);
      setVolumeInput("100.00");
    } else {
      setVolume(Number(numValue.toFixed(2)));
      setVolumeInput(numValue.toFixed(2));
    }
  };

  // Stop Loss handlers
  const handleStopLossChange = (change: number) => {
    if (!stopLoss && change > 0) {
      const defaultStopLoss = 1.0; // Default value
      setStopLoss(defaultStopLoss);
      setStopLossInput(defaultStopLoss.toFixed(digits));
      onStopLossChange?.(defaultStopLoss);
    } else if (stopLoss) {
      const newValue = Math.max(0, stopLoss + change);
      setStopLoss(newValue);
      setStopLossInput(newValue.toFixed(digits));
      if (newValue > 0) {
        onStopLossChange?.(newValue);
      }
    }
  };

  const handleStopLossInputChange = (value: string) => {
    setStopLossInput(value);
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      setStopLoss(numValue);
      onStopLossChange?.(numValue);
    }
  };

  // Take Profit handlers
  const handleTakeProfitChange = (change: number) => {
    if (!takeProfit && change > 0) {
      const defaultTakeProfit = 1.0; // Default value
      setTakeProfit(defaultTakeProfit);
      setTakeProfitInput(defaultTakeProfit.toFixed(digits));
      onTakeProfitChange?.(defaultTakeProfit);
    } else if (takeProfit) {
      const newValue = Math.max(0, takeProfit + change);
      setTakeProfit(newValue);
      setTakeProfitInput(newValue.toFixed(digits));
      if (newValue > 0) {
        onTakeProfitChange?.(newValue);
      }
    }
  };

  const handleTakeProfitInputChange = (value: string) => {
    setTakeProfitInput(value);
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      setTakeProfit(numValue);
      onTakeProfitChange?.(numValue);
    }
  };

  // Price handlers
  const handlePriceChange = (change: number) => {
    if (!price && change > 0) {
      const defaultPrice = 1.0; // Default value
      setPrice(defaultPrice);
      setPriceInput(defaultPrice.toFixed(4));
      onPriceChange?.(defaultPrice);
    } else if (price) {
      const newValue = Math.max(0, price + change);
      setPrice(newValue);
      setPriceInput(newValue.toFixed(4));
      if (newValue > 0) {
        onPriceChange?.(newValue);
      }
    }
  };

  const handlePriceInputChange = (value: string) => {
    setPriceInput(value);
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      setPrice(numValue);
      onPriceChange?.(numValue);
    }
  };

  // Stop Limit Price handlers
  const handleStopLimitPriceChange = (change: number) => {
    if (!stopLimitPrice && change > 0) {
      const defaultStopLimitPrice = 1.0; // Default value
      setStopLimitPrice(defaultStopLimitPrice);
      setStopLimitPriceInput(defaultStopLimitPrice.toFixed(4));
    } else if (stopLimitPrice) {
      const newValue = Math.max(0, stopLimitPrice + change);
      setStopLimitPrice(newValue);
      setStopLimitPriceInput(newValue.toFixed(4));
    }
  };

  const handleStopLimitPriceInputChange = (value: string) => {
    setStopLimitPriceInput(value);
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      setStopLimitPrice(numValue);
    }
  };

  // Investor (read-only) login: no ticket — every entry point (new / modify / close-by) lands here, so
  // this one gate covers them all. Back still works.
  if (isInvestor) {
    return (
      <div className="flex flex-col h-full bg-background text-foreground">
        <div className={`flex items-center border-b border-border ${isDesktop ? "h-7 px-2" : "h-12 px-2"}`}>
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            aria-label="Back"
            className={`hover:bg-transparent text-primary ${isDesktop ? "h-5 w-5 p-0" : ""}`}
          >
            <ChevronLeft size={isDesktop ? 16 : 28} />
          </Button>
          <span className={`flex-1 text-center font-mono ${isDesktop ? "text-[11px]" : "text-sm"}`}>
            {currentSymbol}
          </span>
        </div>
        <InvestorReadOnlyNotice comfortable={!isDesktop} />
      </div>
    );
  }

  if (showSymbolSelection) {
    return (
      <SymbolSelectionScreen
        selectedSymbol={currentSymbol}
        onSelectSymbol={setCurrentSymbol}
        onBack={() => setShowSymbolSelection(false)}
      />
    );
  }

  return (
    <div className="flex flex-col h-full bg-background text-foreground">
      {/* Header */}
      <Card className="rounded-none border-x-0 border-t-0">
        <CardHeader className={isDesktop ? "py-0.5 px-2" : "py-2"}>
          <div className={`flex items-center ${isDesktop ? "gap-2" : "gap-4"}`}>
            {/* Back Arrow */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onBack}
              className={`hover:bg-transparent text-primary ${
                isDesktop ? "h-5 w-5 p-0" : ""
              }`}
            >
              <ChevronLeft size={isDesktop ? 16 : 28} />
            </Button>

            {/* Symbol and Description - Center */}
            <div className="flex-1 flex flex-col items-center">
              <Button
                variant="ghost"
                onClick={() => setShowSymbolSelection(true)}
                className={`flex items-center gap-1 hover:bg-transparent ${
                  isDesktop ? "p-0 h-auto" : "p-0"
                }`}
              >
                <h2
                  className={`${
                    isDesktop ? "text-sm" : "text-lg"
                  } font-semibold`}
                >
                  {currentSymbol || "Select Symbol"}
                </h2>
                <ChevronDown
                  size={isDesktop ? 10 : 16}
                  className="text-muted-foreground"
                />
              </Button>
              {!isDesktop && (
                <p className="text-muted-foreground text-xs">
                  {currentSymbol
                    ? getSymbolDescription(currentSymbol)
                    : "Choose a trading symbol"}
                </p>
              )}
            </div>

            {/* Live bid/ask (mobile) — same pattern as the symbol action sheet */}
            {!isDesktop && bidPrice > 0 && askPrice > 0 ? (
              <div className="flex-shrink-0 rounded-md border border-border bg-muted/30 px-2 py-0.5 text-right font-mono text-xs tabular-nums leading-5">
                <div className="text-blue-400">{formatPrice(bidPrice)}</div>
                <div className="text-red-400">{formatPrice(askPrice)}</div>
              </div>
            ) : (
              <div className={isDesktop ? "w-[20px]" : "w-[44px]"}></div>
            )}
          </div>
        </CardHeader>
      </Card>

      <div className="flex-1 overflow-y-auto">
        <div className={`${isDesktop ? "space-y-1.5 p-2" : "space-y-4 p-4"}`}>
          {/* Order Type Selection */}

          <Select value={orderType} onValueChange={setOrderType}>
            <SelectTrigger
              className={`${
                isDesktop ? "" : "h-8 rounded-lg border-border bg-muted/30 text-xs "
              }${
                orderTypeOptions.find((opt) => opt.value === orderType)
                  ?.color || "text-muted-foreground"
              }`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {orderTypeOptions.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  className={option.color}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Tick chart — rolling bid/ask market context (mobile, proposal 18) */}
          {!isDesktop && type === "new" && currentSymbol && (
            <TickChart symbol={currentSymbol} />
          )}

          {/* Lot size — one-row stepper group (mobile, proposal 18) */}
          {type === "new" && !isDesktop && (
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="flex h-[26px] items-center justify-between border-b border-border/60 px-3 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                <span>Lot size</span>
                <span className="normal-case">step 0.01</span>
              </div>
              <div className="flex items-stretch gap-1.5 p-2">
                {(() => {
                  const adjustments = getLotSizeAdjustments(volume);
                  const step = (change: number, label: string, disabled = false) => (
                    <Button
                      key={label}
                      variant="outline"
                      size="sm"
                      onClick={() => handleVolumeChange(change)}
                      disabled={disabled}
                      className="h-8 flex-1 border-border px-0 font-mono text-[11px] text-muted-foreground hover:text-foreground"
                    >
                      {label}
                    </Button>
                  );
                  return (
                    <>
                      {step(
                        adjustments.decrease[0],
                        `${adjustments.decrease[0]}`
                      )}
                      {step(
                        adjustments.decrease[1],
                        `${adjustments.decrease[1]}`
                      )}
                      <Input
                        type="number"
                        value={volumeInput}
                        onChange={(e) =>
                          handleVolumeInputChange(e.target.value)
                        }
                        onBlur={handleVolumeInputBlur}
                        className="h-8 min-w-0 flex-[1.4] border-border text-center font-mono text-sm font-medium tabular-nums"
                        step="0.01"
                        min="0.01"
                        max="100"
                      />
                      {step(
                        adjustments.increase[0],
                        `+${adjustments.increase[0]}`,
                        volume >= 100
                      )}
                      {step(
                        adjustments.increase[1],
                        `+${adjustments.increase[1]}`,
                        volume >= 100
                      )}
                    </>
                  );
                })()}
              </div>
              <div className="flex items-center justify-center gap-2 pb-2 font-mono text-[10px] text-muted-foreground">
                <button
                  type="button"
                  className="hover:text-foreground"
                  onClick={() => {
                    setVolume(0.01);
                    setVolumeInput("0.01");
                  }}
                >
                  min 0.01
                </button>
                <span>·</span>
                <button
                  type="button"
                  className="hover:text-foreground"
                  onClick={() => {
                    setVolume(1.0);
                    setVolumeInput("1.00");
                  }}
                >
                  standard 1.00
                </button>
                <span>·</span>
                <button
                  type="button"
                  className="hover:text-foreground"
                  onClick={() => {
                    setVolume(10.0);
                    setVolumeInput("10.00");
                  }}
                >
                  max 10.00
                </button>
              </div>
            </div>
          )}

          {/* Volume Control — desktop keeps the existing card */}
          {type === "new" && isDesktop && (
            <Card>
              <CardHeader
                className={`${isDesktop ? "pb-0.5 py-1" : "pb-2"} text-center`}
              >
                <Label
                  className={`${
                    isDesktop ? "text-xs" : "text-base"
                  } font-medium`}
                >
                  Lot Size
                </Label>
              </CardHeader>
              <CardContent
                className={`pt-0 ${isDesktop ? "space-y-1 pb-1" : "space-y-3"}`}
              >
                <div
                  className={`flex items-center justify-center ${
                    isDesktop ? "gap-0.5" : "gap-2"
                  }`}
                >
                  {(() => {
                    const adjustments = getLotSizeAdjustments(volume);
                    return (
                      <>
                        <Button
                          variant="outline"
                          size={isDesktop ? "sm" : "sm"}
                          onClick={() =>
                            handleVolumeChange(adjustments.decrease[0])
                          }
                          className={`${
                            isDesktop
                              ? "min-w-[35px] h-6 text-xs px-1"
                              : "min-w-[60px]"
                          } text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground`}
                        >
                          {adjustments.decrease[0]}
                        </Button>
                        <Button
                          variant="outline"
                          size={isDesktop ? "sm" : "sm"}
                          onClick={() =>
                            handleVolumeChange(adjustments.decrease[1])
                          }
                          className={`${
                            isDesktop
                              ? "min-w-[35px] h-6 text-xs px-1"
                              : "min-w-[60px]"
                          } text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground`}
                        >
                          {adjustments.decrease[1]}
                        </Button>

                        <Input
                          type="number"
                          value={volumeInput}
                          onChange={(e) =>
                            handleVolumeInputChange(e.target.value)
                          }
                          onBlur={handleVolumeInputBlur}
                          className={`${
                            isDesktop ? "w-16 h-6 text-xs px-1" : "w-28 text-lg"
                          } text-center font-medium`}
                          step="0.01"
                          min="0.01"
                          max="100"
                        />

                        <Button
                          variant="outline"
                          size={isDesktop ? "sm" : "sm"}
                          onClick={() => {
                            const newVolume = Math.min(
                              100,
                              volume + adjustments.increase[0]
                            );
                            if (newVolume <= 100)
                              handleVolumeChange(adjustments.increase[0]);
                          }}
                          disabled={volume >= 100}
                          className={`${
                            isDesktop
                              ? "min-w-[35px] h-6 text-xs px-1"
                              : "min-w-[60px]"
                          } text-blue-600 border-blue-600 hover:bg-blue-600 hover:text-foreground dark:text-blue-400 dark:border-blue-400 dark:hover:bg-blue-400`}
                        >
                          +{adjustments.increase[0]}
                        </Button>
                        <Button
                          variant="outline"
                          size={isDesktop ? "sm" : "sm"}
                          onClick={() => {
                            const newVolume = Math.min(
                              100,
                              volume + adjustments.increase[1]
                            );
                            if (newVolume <= 100)
                              handleVolumeChange(adjustments.increase[1]);
                          }}
                          disabled={volume >= 100}
                          className={`${
                            isDesktop
                              ? "min-w-[35px] h-6 text-xs px-1"
                              : "min-w-[60px]"
                          } text-blue-600 border-blue-600 hover:bg-blue-600 hover:text-foreground dark:text-blue-400 dark:border-blue-400 dark:hover:bg-blue-400`}
                        >
                          +{adjustments.increase[1]}
                        </Button>
                      </>
                    );
                  })()}
                </div>

                {!isDesktop && (
                  <div className="flex justify-center gap-4 text-xs">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setVolume(0.01);
                        setVolumeInput("0.01");
                      }}
                      className="text-muted-foreground hover:text-foreground h-auto p-1"
                    >
                      Min: 0.01
                    </Button>
                    <Separator orientation="vertical" className="h-4" />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setVolume(1.0);
                        setVolumeInput("1.00");
                      }}
                      className="text-muted-foreground hover:text-foreground h-auto p-1"
                    >
                      Standard: 1.00
                    </Button>
                    <Separator orientation="vertical" className="h-4" />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setVolume(10.0);
                        setVolumeInput("10.00");
                      }}
                      className="text-muted-foreground hover:text-foreground h-auto p-1"
                    >
                      Max: 10.00
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Order Type Specific Fields */}
          <OrderTypeContentMapper
            orderType={orderType}
            type={type}
            currentSymbol={currentSymbol}
            bidPrice={bidPrice}
            askPrice={askPrice}
            formatPrice={formatPrice}
            price={price}
            priceInput={priceInput}
            stopLimitPrice={stopLimitPrice}
            stopLimitPriceInput={stopLimitPriceInput}
            stopLoss={stopLoss}
            stopLossInput={stopLossInput}
            takeProfit={takeProfit}
            takeProfitInput={takeProfitInput}
            onPriceChange={handlePriceChange}
            onPriceInputChange={handlePriceInputChange}
            onStopLimitPriceChange={handleStopLimitPriceChange}
            onStopLimitPriceInputChange={handleStopLimitPriceInputChange}
            onStopLossChange={handleStopLossChange}
            onStopLossInputChange={handleStopLossInputChange}
            onTakeProfitChange={handleTakeProfitChange}
            onTakeProfitInputChange={handleTakeProfitInputChange}
            onSellByMarket={handleSellByMarket}
            onBuyByMarket={handleBuyByMarket}
            onPlaceOrder={handlePlaceOrder}
            onSaveChanges={handleSaveChanges}
            isTrading={isTrading}
            errorMessage={errorMessage}
            isDesktop={isDesktop}
            volume={volume}
            digits={digits}
            symbolInfo={symbolData?.symbol ?? null}
          />

          {/* Status Messages */}
          {isTrading && (
            <Card>
              <CardContent className={isDesktop ? "p-2" : "p-4"}>
                <div
                  className={`${
                    isDesktop ? "text-xs" : "text-sm"
                  } text-muted-foreground text-center`}
                >
                  Processing order...
                </div>
              </CardContent>
            </Card>
          )}

          {/* Error Message */}
          {errorMessage && (
            <Card className="border-red-200 dark:border-red-800">
              <CardContent className={isDesktop ? "p-2" : "p-4"}>
                <div
                  className={`${
                    isDesktop ? "text-xs" : "text-sm"
                  } text-red-600 dark:text-red-400 text-center`}
                >
                  {errorMessage}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
